import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  model,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { SvgComponent } from '@shared/components/svg';
import { LabelKey } from '@shared/models';
import { UrlParamsService } from '@shared/services/url-params';
import { Subject, takeUntil } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';

@Component({
  selector: 'app-search',
  imports: [ReactiveFormsModule, SvgComponent, TranslatePipe, NgClass],
  templateUrl: './search.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchComponent implements OnInit, OnDestroy {
  readonly queryParamKey = input.required<string>();
  readonly debounceDelayMs = input<number>(null!);
  readonly dropdownEnabled = input(false);
  readonly onOptionSelected = output<LabelKey>();
  readonly isDropdownOpen = model(false);
  readonly searchSuggestions = input<LabelKey[]>();
  readonly placeholder = input<string>('');
  readonly searchControl = new FormControl('');
  readonly isMinLengthError = signal(false);
  private readonly destroy$ = new Subject<void>();
  private readonly urlParamsService = inject(UrlParamsService);

  constructor() {
    effect(() => {
      if (this.debounceDelayMs()) {
        this.searchControl.valueChanges
          .pipe(
            takeUntil(this.destroy$),
            debounceTime(this.debounceDelayMs()),
            tap((value) => {
              this.updateQueryParam(value ?? '');
            })
          )
          .subscribe();
      }
    });
    effect(() => {
      if (this.searchSuggestions() && this.searchSuggestions()!.length > 0) {
        this.isDropdownOpen.set(true);
      }
    });
  }

  ngOnInit(): void {
    const hasValue = this.urlParamsService.param(this.queryParamKey())();
    if (hasValue) {
      this.searchControl.setValue(hasValue);
      if (hasValue.length < 3) {
        this.isMinLengthError.set(true);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  submitSearch(): void {
    this.isMinLengthError.set(false);
    if (this.isDropdownOpen()) {
      if (this.searchSuggestions() && this.searchSuggestions()!.length > 0) {
        const option = this.searchSuggestions()![0] as LabelKey;
        this.selectSuggestion(option);
      }
    } else {
      const value = this.searchControl.value!;
      this.updateQueryParam(value);
    }
  }

  selectSuggestion(option: LabelKey) {
    this.isDropdownOpen.set(false);
    this.searchControl.setValue(option.label, { emitEvent: false });
    this.onOptionSelected.emit(option);
  }

  private updateQueryParam(value: string): void {
    this.isMinLengthError.set(false);
    if (value.length === 0) {
      this.isDropdownOpen.set(false);
      this.urlParamsService.removeParams(this.queryParamKey());
    } else if (value.length < 3) {
      this.isDropdownOpen.set(false);
      this.isMinLengthError.set(true);
    } else {
      this.urlParamsService.mergeParams({ [this.queryParamKey()]: value });
    }
  }
}
